export type SubmissionTableLabColumnKey =
  | 'submissionStatus'
  | 'evaluationStatus'
  | 'id'
  | 'submitDate'
  | 'insured'
  | 'underwriter'
  | 'quoteId'
  | 'applicationType'
  | 'policyNumber'
  | 'line'
  | 'submissionType'
  | 'riskAppetite'
  | 'bindPropensity'
  | 'dataQualityScore'
  | 'effectiveDate'
  | 'annualRevenue';

export type SubmissionTableLabFilterKey = SubmissionTableLabColumnKey;

export type SubmissionTableLabSortDirection = 'asc' | 'desc';

export type SubmissionTableLabSortState = {
  key: SubmissionTableLabColumnKey;
  direction: SubmissionTableLabSortDirection;
};

export type SubmissionTableLabColumnVisibility = Record<SubmissionTableLabColumnKey, boolean>;

export type SubmissionTableLabColumnOption = {
  key: SubmissionTableLabColumnKey;
  label: string;
  defaultSelected: boolean;
  width: number;
  minWidth: number;
  maxWidth: number;
};

export type SubmissionTableLabRow = {
  id: string;
  quoteId: string;
  submissionStatus: string;
  statusCurrentStage: number;
  statusStageTotal: number;
  applicationType: string;
  policyNumber: string;
  insured: string;
  underwriter: string;
  agency: string;
  line: string;
  submissionType: string;
  effectiveDate: string;
  annualRevenue: number | null;
  createdAt: string;
  createdAtValue: string | null;
  evaluationStatus: string;
  riskAppetite: string;
  bindPropensity: string;
  dataQualityScore: string;
  actionStatus: {
    isProcessed: boolean;
    hasDocument: boolean;
    riskAppetiteScore?: string | null;
    evaluationStatus?: string | null;
  } | null;
  isCheckingActionStatus: boolean;
};

export type SubmissionTableLabFilters = Partial<Record<SubmissionTableLabFilterKey, string>>;

export type SubmissionTableLabActiveFilterItem = {
  key: SubmissionTableLabFilterKey;
  label: string;
  value: string;
  values: string[];
  isMultiValue: boolean;
};

export const SUBMISSION_TABLE_LAB_STATUS_OPTIONS = ['BOUND', 'CLOSED', 'QUOTE', 'SUBMISSION', 'DECLINE'] as const;

export const SUBMISSION_TABLE_LAB_APPLICATION_OPTIONS = ['499', '485', '492', '495', '483'] as const;

export const SUBMISSION_TABLE_LAB_COLUMN_OPTIONS: ReadonlyArray<SubmissionTableLabColumnOption> = [
  { key: 'submissionStatus', label: 'Sub Status', defaultSelected: true, width: 140, minWidth: 140, maxWidth: 180 },
  { key: 'evaluationStatus', label: 'Evaluation Status', defaultSelected: false, width: 160, minWidth: 160, maxWidth: 210 },
  { key: 'id', label: 'Submission', defaultSelected: false, width: 120, minWidth: 110, maxWidth: 150 },
  { key: 'submitDate', label: 'Submit Date', defaultSelected: true, width: 150, minWidth: 150, maxWidth: 200 },
  { key: 'insured', label: 'Insured', defaultSelected: true, width: 220, minWidth: 180, maxWidth: 320 },
  { key: 'underwriter', label: 'Underwriter', defaultSelected: true, width: 170, minWidth: 150, maxWidth: 240 },
  { key: 'quoteId', label: 'Quote #', defaultSelected: true, width: 130, minWidth: 120, maxWidth: 180 },
  { key: 'applicationType', label: 'App Type', defaultSelected: true, width: 130, minWidth: 120, maxWidth: 180 },
  { key: 'policyNumber', label: 'Policy Number', defaultSelected: false, width: 150, minWidth: 130, maxWidth: 200 },
  { key: 'line', label: 'Line', defaultSelected: false, width: 130, minWidth: 120, maxWidth: 200 },
  { key: 'submissionType', label: 'Type', defaultSelected: false, width: 150, minWidth: 130, maxWidth: 220 },
  { key: 'riskAppetite', label: 'Risk Appetite', defaultSelected: true, width: 135, minWidth: 120, maxWidth: 170 },
  { key: 'bindPropensity', label: 'Bind Propensity', defaultSelected: false, width: 135, minWidth: 120, maxWidth: 170 },
  { key: 'dataQualityScore', label: 'Data Quality', defaultSelected: true, width: 135, minWidth: 120, maxWidth: 170 },
  { key: 'effectiveDate', label: 'Effective Date', defaultSelected: true, width: 150, minWidth: 140, maxWidth: 200 },
  { key: 'annualRevenue', label: 'Revenue', defaultSelected: false, width: 140, minWidth: 130, maxWidth: 220 }
];

export const SUBMISSION_TABLE_LAB_COLUMN_ORDER: SubmissionTableLabColumnKey[] = [
  'submissionStatus',
  'evaluationStatus',
  'id',
  'submitDate',
  'insured',
  'underwriter',
  'quoteId',
  'applicationType',
  'policyNumber',
  'line',
  'submissionType',
  'riskAppetite',
  'bindPropensity',
  'dataQualityScore',
  'effectiveDate',
  'annualRevenue'
];

export const SUBMISSION_TABLE_LAB_SAMPLE_ROWS: SubmissionTableLabRow[] = [
  {
    id: 'SUB-1001',
    quoteId: 'Q-80021',
    submissionStatus: 'SUBMISSION',
    statusCurrentStage: 1,
    statusStageTotal: 5,
    applicationType: '499',
    policyNumber: 'PN-88921',
    insured: 'Northline Logistics LLC',
    underwriter: 'Maya Chen',
    line: 'CAS',
    submissionType: 'New Business',
    effectiveDate: '2026-10-01',
    riskAppetite: '24',
    bindPropensity: '72%',
    dataQualityScore: 'A',
    evaluationStatus: 'PROCEED',
    annualRevenue: 1840000,
    createdAt: 'Sep 11, 2026',
    createdAtValue: '2026-09-11',
    agency: 'Northline',
    actionStatus: {
      isProcessed: false,
      hasDocument: true,
      evaluationStatus: 'PROCEED'
    },
    isCheckingActionStatus: false
  },
  {
    id: 'SUB-1002',
    quoteId: 'Q-80022',
    submissionStatus: 'QUOTE',
    statusCurrentStage: 2,
    statusStageTotal: 4,
    applicationType: '492',
    policyNumber: 'PN-88925',
    insured: 'Harbor Peak Manufacturing',
    underwriter: 'Dylan Foster',
    line: 'Property',
    submissionType: 'Renewal',
    effectiveDate: '2026-10-15',
    riskAppetite: '18',
    bindPropensity: '61%',
    dataQualityScore: 'B',
    evaluationStatus: 'FLAGGED',
    annualRevenue: 4200000,
    createdAt: 'Sep 08, 2026',
    createdAtValue: '2026-09-08',
    agency: 'Harbor Peak',
    actionStatus: {
      isProcessed: false,
      hasDocument: true,
      evaluationStatus: 'FLAGGED'
    },
    isCheckingActionStatus: false
  },
  {
    id: 'SUB-1003',
    quoteId: 'Q-80023',
    submissionStatus: 'BOUND',
    statusCurrentStage: 3,
    statusStageTotal: 4,
    applicationType: '485',
    policyNumber: 'PN-88930',
    insured: 'Arden Foods Group',
    underwriter: 'Maya Chen',
    line: 'Excess',
    submissionType: 'Endorsement',
    effectiveDate: '2026-09-20',
    riskAppetite: '28',
    bindPropensity: '84%',
    dataQualityScore: 'A',
    evaluationStatus: 'PROCEED',
    annualRevenue: 980000,
    createdAt: 'Sep 06, 2026',
    createdAtValue: '2026-09-06',
    agency: 'Arden Foods',
    actionStatus: {
      isProcessed: true,
      hasDocument: true,
      evaluationStatus: 'PROCEED'
    },
    isCheckingActionStatus: false
  },
  {
    id: 'SUB-1004',
    quoteId: 'Q-80024',
    submissionStatus: 'DECLINE',
    statusCurrentStage: 2,
    statusStageTotal: 3,
    applicationType: '495',
    policyNumber: 'PN-88933',
    insured: 'Cypress Hotel Partners',
    underwriter: 'Sofia Patel',
    line: 'Property',
    submissionType: 'New Business',
    effectiveDate: '2026-09-28',
    riskAppetite: '11',
    bindPropensity: '32%',
    dataQualityScore: 'C',
    evaluationStatus: 'DECLINE',
    annualRevenue: 760000,
    createdAt: 'Sep 04, 2026',
    createdAtValue: '2026-09-04',
    agency: 'Cypress Hotel',
    actionStatus: {
      isProcessed: false,
      hasDocument: true,
      evaluationStatus: 'DECLINE'
    },
    isCheckingActionStatus: false
  },
  {
    id: 'SUB-1005',
    quoteId: 'Q-80025',
    submissionStatus: 'SUBMISSION',
    statusCurrentStage: 1,
    statusStageTotal: 3,
    applicationType: '483',
    policyNumber: 'PN-88937',
    insured: 'Blue Ridge Energy Co.',
    underwriter: 'Dylan Foster',
    line: 'Cargo',
    submissionType: 'Renewal',
    effectiveDate: '2026-10-10',
    riskAppetite: '20',
    bindPropensity: '56%',
    dataQualityScore: 'B',
    evaluationStatus: 'FLAGGED',
    annualRevenue: 2200000,
    createdAt: 'Sep 03, 2026',
    createdAtValue: '2026-09-03',
    agency: 'Blue Ridge',
    actionStatus: {
      isProcessed: false,
      hasDocument: true,
      evaluationStatus: 'FLAGGED'
    },
    isCheckingActionStatus: false
  },
  {
    id: 'SUB-1006',
    quoteId: 'Q-80026',
    submissionStatus: 'QUOTE',
    statusCurrentStage: 2,
    statusStageTotal: 5,
    applicationType: '499',
    policyNumber: 'PN-88940',
    insured: 'Summit Healthcare Network',
    underwriter: 'Maya Chen',
    line: 'GL',
    submissionType: 'New Business',
    effectiveDate: '2026-10-05',
    riskAppetite: '26',
    bindPropensity: '77%',
    dataQualityScore: 'A',
    evaluationStatus: 'PROCEED',
    annualRevenue: 5400000,
    createdAt: 'Sep 01, 2026',
    createdAtValue: '2026-09-01',
    agency: 'Summit Healthcare',
    actionStatus: {
      isProcessed: true,
      hasDocument: true,
      evaluationStatus: 'PROCEED'
    },
    isCheckingActionStatus: false
  },
  {
    id: 'SUB-1007',
    quoteId: 'Q-80027',
    submissionStatus: 'CLOSED',
    statusCurrentStage: 3,
    statusStageTotal: 4,
    applicationType: '492',
    policyNumber: 'PN-88941',
    insured: 'Riverside School District',
    underwriter: 'Sofia Patel',
    line: 'Property',
    submissionType: 'Renewal',
    effectiveDate: '2026-09-25',
    riskAppetite: '15',
    bindPropensity: '49%',
    dataQualityScore: 'C',
    evaluationStatus: 'FLAGGED',
    annualRevenue: 3150000,
    createdAt: 'Aug 30, 2026',
    createdAtValue: '2026-08-30',
    agency: 'Riverside Schools',
    actionStatus: {
      isProcessed: false,
      hasDocument: true,
      evaluationStatus: 'FLAGGED'
    },
    isCheckingActionStatus: false
  },
  {
    id: 'SUB-1008',
    quoteId: 'Q-80028',
    submissionStatus: 'BOUND',
    statusCurrentStage: 4,
    statusStageTotal: 3,
    applicationType: '495',
    policyNumber: 'PN-88943',
    insured: 'Metro Freight Brokers',
    underwriter: 'Dylan Foster',
    line: 'Excess',
    submissionType: 'Renewal',
    effectiveDate: '2026-09-18',
    riskAppetite: '29',
    bindPropensity: '91%',
    dataQualityScore: 'A',
    evaluationStatus: 'PROCEED',
    annualRevenue: 6820000,
    createdAt: 'Aug 28, 2026',
    createdAtValue: '2026-08-28',
    agency: 'Metro Freight',
    actionStatus: {
      isProcessed: true,
      hasDocument: true,
      evaluationStatus: 'PROCEED'
    },
    isCheckingActionStatus: false
  }
];
